/**
 * assets/cart-drawer.js
 *
 * 負責 Cart Drawer 的開關、渲染（大圖 + Item / Size / Quantity / Price）、
 * 加減數量、刪除商品、Summary（Subtotal / Shipping / Tax / Total）、折扣碼。
 *
 * 效能設計：
 *   - Optimistic UI：點擊 +/- 或刪除時，先用前端已知的單價立即更新該行與 Summary，
 *     不等網路回應，感覺是「秒更新」。
 *   - 刪除商品（或數量減到 0）會先播放淡出 + 高度收合的動畫，動畫結束後才真正把
 *     DOM 節點移除，避免商品瞬間消失、下面商品瞬間往上跳的突兀感。
 *   - Debounce：真正打 /cart/change.js 的請求會延遲 350ms 送出，如果使用者在這段時間內
 *     又點擊了同一個商品，只會送出「最後一次」的目標數量，避免連點狂發請求。
 *   - 每個商品 key 都有請求序號（requestSeq），如果舊的請求比新的請求晚回來，會被丟棄，
 *     避免因為網路順序不一致而讓畫面被舊資料蓋掉。
 *   - Debounce 結束後拿到的伺服器權威資料，會用「輕量校正」（reconcile）修正任何 optimistic
 *     計算跟伺服器實際邏輯（例如數量門檻折扣、庫存限制）之間的誤差——只更新有變化的數字，
 *     不會砍掉重建整個清單 DOM，避免捲動位置跳掉、圖片重新閃爍造成的頓挫感。
 *
 * 對外事件：
 *   - 監聽 document 上的 'cart:updated'（其他地方 quick-add 成功時 dispatch）
 *     → 會自動刷新並打開 drawer，同時 dispatch 'cart-drawer:toast' 讓外部顯示提示。
 *     如果 detail.silent 為 true（例如 Quick View 加入購物車後只想更新角標，
 *     不想跳出整個 drawer），則不會 refresh/開啟 drawer、也不會顯示 toast。
 *   - 發生更新失敗時會 dispatch 'cart-drawer:error'，detail.message 為錯誤訊息。
 *
 * 對外方法：window.CartDrawer.open() / close() / refresh(openAfter) / updateItem(key, qty)
 *
 * 折扣碼說明：
 *   Shopify 的 Ajax Cart API（/cart.js、/cart/change.js）不會反映「手動輸入的折扣碼」，
 *   折扣碼是在 Checkout 階段才會生效。這裡的做法是把使用者導向
 *   /discount/{code}?redirect=... 讓 Shopify 設定折扣 session，
 *   之後不管是回到原頁還是前往 Checkout，該折扣碼都會生效。
 *   無法在 Drawer 內即時預覽套用折扣後的金額（這是 Shopify Ajax Cart 的限制，不是這支程式的 bug）。
 */
window.CartDrawer = (function () {
  var cartDrawer,
    cartOverlay,
    itemsEl,
    itemCountEl,
    subtotalEl,
    shippingEl,
    totalEl,
    shippingNoteEl,
    cartCheckoutBtn,
    cartToggleBtn,
    cartCloseBtn,
    promoForm,
    promoInput;

  // 最新一次從伺服器拿到的權威購物車資料。
  // optimistic 更新時也會同步修改這份資料，讓下一次計算有正確的基準。
  var cartState = null;

  // key -> setTimeout id（debounce 用）
  var pendingTimers = {};
  // key -> 目前排隊中、還沒送出的目標數量
  var pendingQtyByKey = {};
  // key -> 目前該 key 最新的請求序號，用來丟棄過期回應
  var requestSeq = {};

  var DEBOUNCE_MS = 350;
  var REMOVE_ANIM_MS = 220;

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  function open() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartOverlay.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
  }

  function close() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartOverlay.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
  }

  function updateShippingInfo(cart) {
    var threshold = parseInt(cartDrawer && cartDrawer.getAttribute('data-free-shipping-threshold'), 10) || 0;
    if (!threshold) return;

    if (shippingNoteEl) {
      if (cart.items_subtotal_price >= threshold) {
        shippingNoteEl.textContent = 'You’ve unlocked free shipping!';
      } else {
        var remaining = threshold - cart.items_subtotal_price;
        shippingNoteEl.textContent = 'Add ' + formatMoney(remaining) + ' more for free shipping';
      }
    }

    if (shippingEl) {
      shippingEl.textContent = cart.items_subtotal_price >= threshold ? 'Free' : 'Calculated at checkout';
    }
  }

  // ---- 共用的 Summary 更新（optimistic 跟伺服器權威資料都會呼叫這個） ----
  function updateSummaryDisplay(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    if (itemCountEl) {
      itemCountEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    updateShippingInfo(cart);

    if (cartCheckoutBtn) {
      cartCheckoutBtn.classList.toggle('is-disabled', cart.item_count === 0);
    }
  }

  function showEmptyState() {
    if (itemsEl) {
      itemsEl.innerHTML = '<p class="cart-drawer-empty">Your cart is empty.</p>';
    }
  }

  // ---- 完整渲染：初次載入、加入購物車（非 silent）事件都會用到。
  //      用 data-item-key 做 diff，只新增/移除真正有變化的行，
  //      不會把整個清單砍掉重建（避免捲動位置跳掉、圖片重新閃爍）。----
  function render(cart) {
    cartState = cart;
    updateSummaryDisplay(cart);

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      showEmptyState();
      return;
    }

    // 如果目前是「購物車是空的」那段提示文字，先清掉，準備放商品列
    if (itemsEl.querySelector('.cart-drawer-empty')) {
      itemsEl.innerHTML = '';
    }

    var newKeys = cart.items.map(function (item) { return String(item.key); });

    // 移除已經不在購物車裡的商品列（用 data-item-key 判斷，不是 data-key）
    Array.prototype.slice.call(itemsEl.children).forEach(function (row) {
      var rowKey = row.getAttribute('data-item-key');
      if (rowKey && newKeys.indexOf(rowKey) === -1) {
        animateRemoveRow(rowKey);
      }
    });

    // 更新既有商品列，或插入新商品列
    cart.items.forEach(function (item) {
      var existing = itemsEl.querySelector('[data-item-key="' + item.key + '"]');
      if (existing) {
        patchRow(item);
      } else {
        itemsEl.appendChild(buildRow(item));
      }
    });
  }

  // ---- 輕量校正：debounce 結束、拿到伺服器權威資料後用這個，
  //      只更新有變化的數字，不砍掉重建整個清單，
  //      避免捲動位置跳掉、圖片重新閃爍造成的頓挫感。----
  function reconcile(cart) {
    cartState = cart;
    updateSummaryDisplay(cart);

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      showEmptyState();
      return;
    }

    var domKeys = Array.prototype.map.call(
      itemsEl.querySelectorAll('[data-item-key]'),
      function (el) { return el.getAttribute('data-item-key'); }
    );
    var stateKeys = cart.items.map(function (item) { return item.key; });

    var sameStructure =
      domKeys.length === stateKeys.length &&
      domKeys.every(function (key, index) { return key === stateKeys[index]; });

    if (sameStructure) {
      // 畫面上的商品順序、key 都跟伺服器一致，只更新每一行的數量/價格文字
      cart.items.forEach(function (item) { patchRow(item); });
    } else {
      // 結構對不上（例如同時開了兩個分頁操作同一個購物車），保險起見整個重畫一次
      itemsEl.innerHTML = '';
      cart.items.forEach(function (item) { itemsEl.appendChild(buildRow(item)); });
    }
  }

  function buildRow(item) {
    var image = item.image ? item.image.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';
    var size = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '—';
    var subtitle = item.product_type || item.variant_title || '';

    var row = document.createElement('div');
    row.className = 'cart-drawer-item';
    row.setAttribute('data-item-key', item.key);
    row.innerHTML =
      '<div class="cart-drawer-item-main">' +
        '<div class="cart-drawer-item-image"><img src="' + image + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy"></div>' +
        '<div class="cart-drawer-item-info">' +
          '<a class="cart-drawer-item-title" href="' + item.url + '">' + escapeHtml(item.product_title) + '</a>' +
          (subtitle ? '<div class="cart-drawer-item-subtitle">' + escapeHtml(subtitle) + '</div>' : '') +
        '</div>' +
      '</div>' +
      '<div class="cart-drawer-item-size">' + escapeHtml(size) + '</div>' +
      '<div class="cart-drawer-item-qty">' +
        '<button type="button" class="cart-qty-btn" data-qty-decrease data-key="' + item.key + '" aria-label="Decrease quantity">−</button>' +
        '<span class="cart-qty-value">' + item.quantity + '</span>' +
        '<button type="button" class="cart-qty-btn" data-qty-increase data-key="' + item.key + '" aria-label="Increase quantity">+</button>' +
      '</div>' +
      '<div class="cart-drawer-item-price">' + formatMoney(item.final_line_price) + '</div>' +
      '<button type="button" class="cart-drawer-item-remove" data-remove-item data-key="' + item.key + '" aria-label="Remove item">×</button>';
    return row;
  }

  // ---- 局部更新：只改動有變化的那一行，不重新渲染整個清單 ----
  function patchRow(item) {
    if (!itemsEl) return;
    var row = itemsEl.querySelector('[data-item-key="' + item.key + '"]');
    if (!row) return;

    var qtyValueEl = row.querySelector('.cart-qty-value');
    var priceEl = row.querySelector('.cart-drawer-item-price');

    if (qtyValueEl) qtyValueEl.textContent = item.quantity;
    if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);
  }

  // ---- 移除商品列的動畫：先淡出 + 高度收合，動畫結束後才真正從 DOM 移除 ----
  //      這是「刪除商品感覺卡」的關鍵修正：原本是直接 row.remove()，
  //      商品瞬間消失、下面商品瞬間往上跳；現在改成平滑過渡。
  function animateRemoveRow(key, onDone) {
    if (!itemsEl) {
      if (onDone) onDone();
      return;
    }

    var row = itemsEl.querySelector('[data-item-key="' + key + '"]');
    if (!row) {
      if (onDone) onDone();
      return;
    }

    var startHeight = row.offsetHeight;

    // 先固定高度，才能讓「高度收合」的 transition 生效（auto 高度沒辦法做 transition）
    row.style.height = startHeight + 'px';
    row.style.overflow = 'hidden';
    row.classList.add('is-removing');

    // 強制瀏覽器 reflow 一次，確保上面設定的高度先套用，
    // 接下來改成 0 才會被瀏覽器當成「有變化」而觸發 transition
    // eslint-disable-next-line no-unused-expressions
    row.offsetHeight;

    requestAnimationFrame(function () {
      row.style.height = '0px';
      row.style.marginTop = '0px';
      row.style.marginBottom = '0px';
      row.style.paddingTop = '0px';
      row.style.paddingBottom = '0px';
      row.style.borderBottomWidth = '0px';
    });

    setTimeout(function () {
      row.remove();
      if (itemsEl && !itemsEl.querySelector('[data-item-key]')) {
        showEmptyState();
      }
      if (onDone) onDone();
    }, REMOVE_ANIM_MS);
  }

  function updateSummaryFromState() {
    if (!cartState) return;

    var subtotal = cartState.items.reduce(function (sum, i) { return sum + i.final_line_price; }, 0);

    // Optimistic 階段先用 subtotal 當作 total 的近似值；
    // debounce 結束後的 reconcile() 會用伺服器算好的 cart.total_price 修正誤差
    // （例如購物車層級的折扣，不是每個商品單獨算的）。
    updateSummaryDisplay({
      item_count: cartState.item_count,
      items_subtotal_price: subtotal,
      total_price: subtotal
    });
  }

  function findItem(key) {
    if (!cartState) return null;
    for (var i = 0; i < cartState.items.length; i++) {
      if (cartState.items[i].key === key) return cartState.items[i];
    }
    return null;
  }

  // ---- Optimistic：立即更新畫面，debounce 後才真正打 API ----
  function handleQtyChange(key, delta) {
    var item = findItem(key);
    if (!item) return;

    var unitPrice = item.quantity > 0 ? item.final_line_price / item.quantity : 0;
    var newQty = Math.max(item.quantity + delta, 0);

    if (newQty === 0) {
      removeItemOptimistic(key);
      return;
    }

    item.quantity = newQty;
    item.final_line_price = Math.round(unitPrice * newQty);
    cartState.item_count += delta;

    patchRow(item);
    updateSummaryFromState();

    scheduleUpdate(key, newQty);
  }

  function removeItemOptimistic(key) {
    var item = findItem(key);
    if (!item) return;

    // 資料層面（數量、小計、Summary）立即更新，不用等動畫
    cartState.item_count -= item.quantity;
    cartState.items = cartState.items.filter(function (i) { return i.key !== key; });
    updateSummaryFromState();

    // 畫面層面：播放淡出 + 收合動畫，動畫結束才真正移除 DOM 節點
    animateRemoveRow(key);

    scheduleUpdate(key, 0);
  }

  function scheduleUpdate(key, quantity) {
    pendingQtyByKey[key] = quantity;

    if (pendingTimers[key]) clearTimeout(pendingTimers[key]);
    pendingTimers[key] = setTimeout(function () {
      var qty = pendingQtyByKey[key];
      delete pendingTimers[key];
      delete pendingQtyByKey[key];
      sendUpdate(key, qty);
    }, DEBOUNCE_MS);
  }

  function sendUpdate(key, quantity) {
    var seq = (requestSeq[key] || 0) + 1;
    requestSeq[key] = seq;

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // 使用者在等待期間又觸發了更新，這筆回應已經過期了，丟棄不處理
        if (requestSeq[key] !== seq) return;
        reconcile(cart);
      })
      .catch(function () {
        if (requestSeq[key] !== seq) return;
        document.dispatchEvent(new CustomEvent('cart-drawer:error', {
          detail: { message: 'Failed to update cart. Please try again.' }
        }));
        // 失敗就以伺服器現況為準重新整理，撤銷掉前面 optimistic 的畫面
        refresh(false);
      });
  }

  // 供外部呼叫（例如商品頁直接指定數量），走一般（非 debounce）流程
  function updateItem(key, quantity) {
    return sendUpdate(key, quantity);
  }

  function refresh(openAfter) {
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        render(cart);
        if (openAfter) open();
        return cart;
      })
      .catch(function () {});
  }

  function applyDiscountCode(code) {
    if (!code) return;
    var redirectTo = window.location.pathname + window.location.search;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirectTo);
  }

  function bindEvents() {
    if (cartToggleBtn) {
      cartToggleBtn.addEventListener('click', function () {
        refresh(true);
      });
    }

    if (cartCloseBtn) cartCloseBtn.addEventListener('click', close);
    if (cartOverlay) cartOverlay.addEventListener('click', close);

    if (itemsEl) {
      itemsEl.addEventListener('click', function (e) {
        var increaseBtn = e.target.closest('[data-qty-increase]');
        var decreaseBtn = e.target.closest('[data-qty-decrease]');
        var removeBtn = e.target.closest('[data-remove-item]');

        if (increaseBtn) {
          handleQtyChange(increaseBtn.getAttribute('data-key'), 1);
        } else if (decreaseBtn) {
          handleQtyChange(decreaseBtn.getAttribute('data-key'), -1);
        } else if (removeBtn) {
          removeItemOptimistic(removeBtn.getAttribute('data-key'));
        }
      });
    }

    if (promoForm) {
      promoForm.addEventListener('submit', function (e) {
        e.preventDefault();
        applyDiscountCode(promoInput.value.trim());
      });
    }

    // 其他地方（例如商品卡片的快速加入購物車）成功後可以：
    // document.dispatchEvent(new CustomEvent('cart:updated', { detail: { product_title: '...' } }))
    // 如果只是想更新角標、不想整個 drawer 跳出來（例如 Quick View 加完購物車），
    // 可以在 detail 裡加上 silent: true。
    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.silent) return;

      refresh(true);
      document.dispatchEvent(new CustomEvent('cart-drawer:toast', {
        detail: {
          message: (e.detail && e.detail.product_title ? e.detail.product_title : '商品') + ' 已加入購物車'
        }
      }));
    });
  }

  function init() {
    cartDrawer = document.getElementById('cart-drawer');
    cartOverlay = document.getElementById('cart-drawer-overlay');
    itemsEl = document.getElementById('cart-drawer-items');
    itemCountEl = document.getElementById('cart-drawer-item-count');
    subtotalEl = document.getElementById('cart-summary-subtotal');
    shippingEl = document.getElementById('cart-summary-shipping');
    totalEl = document.getElementById('cart-summary-total');
    shippingNoteEl = document.getElementById('cart-drawer-shipping-note');
    cartCheckoutBtn = document.getElementById('cart-drawer-checkout-btn');
    cartToggleBtn = document.getElementById('marketplace-cart-toggle');
    cartCloseBtn = document.getElementById('cart-drawer-close');
    promoForm = document.getElementById('cart-drawer-promo-form');
    promoInput = document.getElementById('cart-drawer-promo-input');

    bindEvents();

    // 進站時先同步購物車數量與金額（不開啟 drawer）
    refresh(false);
  }

  document.addEventListener('DOMContentLoaded', init);

  return {
    open: open,
    close: close,
    refresh: refresh,
    updateItem: updateItem
  };
})();